import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { mountGuideService, uploadService } from '../services/api';
import type {
    MountGuideDetails,
    MountGuideMount,
    MountGuideRarityCategory,
    MountGuideRegion,
    MountGuideSection,
} from '../types';
import { slugify } from '../utils/guideLocale';
import MountGuideMountForm from '../components/admin/MountGuideMountForm';
import {
    BARTOLO_DETAILS,
    DEFAULT_MOUNT_DETAILS,
    parseMountDetails,
    stringifyMountDetails,
} from '../utils/mountGuideDetails';
import { resolvePublicMediaUrl } from '../utils/newsContent';
import { invalidateMountGuideCache } from '../utils/mountGuideCache';
import '../styles/AdminGuides.css';

type Tab = 'sections' | 'regions' | 'rarities' | 'mounts';

const emptySection: MountGuideSection = {
    slug: '',
    titleEn: '',
    titlePtBr: '',
    sortOrder: 0,
    isVisible: true,
};

const emptyRegion: MountGuideRegion = {
    sectionId: null,
    slug: '',
    titleEn: '',
    titlePtBr: '',
    thumbnailUrl: '',
    bannerUrl: '',
    sortOrder: 0,
    isVisible: true,
};

const emptyRarity: MountGuideRarityCategory = {
    slug: '',
    titleEn: '',
    titlePtBr: '',
    accentColor: '#3b82f6',
    sortOrder: 0,
    isVisible: true,
};

const emptyMount: MountGuideMount = {
    regionId: 0,
    rarityCategoryId: 0,
    slug: '',
    titleEn: '',
    titlePtBr: '',
    iconUrl: '',
    heroImageUrl: '',
    descriptionEn: '',
    descriptionPtBr: '',
    detailsJson: stringifyMountDetails(DEFAULT_MOUNT_DETAILS),
    sortOrder: 0,
    isPublished: true,
};

const AdminMountGuide: React.FC = () => {
    const [tab, setTab] = useState<Tab>('regions');
    const [sections, setSections] = useState<MountGuideSection[]>([]);
    const [regions, setRegions] = useState<MountGuideRegion[]>([]);
    const [rarities, setRarities] = useState<MountGuideRarityCategory[]>([]);
    const [mounts, setMounts] = useState<MountGuideMount[]>([]);
    const [secForm, setSecForm] = useState(emptySection);
    const [regForm, setRegForm] = useState(emptyRegion);
    const [rarForm, setRarForm] = useState(emptyRarity);
    const [mountForm, setMountForm] = useState(emptyMount);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [s, r, ra, m] = await Promise.all([
                mountGuideService.getAdminSections(),
                mountGuideService.getAdminRegions(),
                mountGuideService.getAdminRarities(),
                mountGuideService.getAdminMounts(),
            ]);
            setSections(s);
            setRegions(r);
            setRarities(ra);
            setMounts(m);
            setMountForm((prev) => ({
                ...prev,
                regionId: prev.regionId || r[0]?.id || 0,
                rarityCategoryId: prev.rarityCategoryId || ra[0]?.id || 0,
            }));
        } catch {
            setError('Não foi possível carregar o guia de montarias.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    const uploadField = async (file: File, field: 'thumbnailUrl' | 'bannerUrl' | 'iconUrl' | 'heroImageUrl') => {
        const result = await uploadService.uploadImage(file);
        if (tab === 'regions') setRegForm((p) => ({ ...p, [field]: result.url }));
        if (tab === 'mounts') setMountForm((p) => ({ ...p, [field]: result.url }));
    };

    const mountDetails = useMemo(
        () => parseMountDetails(mountForm.detailsJson),
        [mountForm.detailsJson]
    );

    const patchMountDetails = (partial: Partial<MountGuideDetails>) => {
        setMountForm((prev) => ({
            ...prev,
            detailsJson: stringifyMountDetails({ ...parseMountDetails(prev.detailsJson), ...partial }),
        }));
    };

    const uploadMapImage = async (file: File) => {
        const result = await uploadService.uploadImage(file);
        patchMountDetails({ mapImageUrl: result.url });
    };

    const mapPreviewUrl = resolvePublicMediaUrl(mountDetails.mapImageUrl);

    const saveSection = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                slug: secForm.slug.trim() || slugify(secForm.titlePtBr || secForm.titleEn),
                titleEn: secForm.titleEn.trim(),
                titlePtBr: secForm.titlePtBr.trim(),
                sortOrder: Number(secForm.sortOrder) || 0,
                isVisible: secForm.isVisible,
            };
            if (secForm.id) await mountGuideService.updateSection(secForm.id, payload);
            else await mountGuideService.createSection(payload);
            invalidateMountGuideCache();
            await loadAll();
            setSecForm(emptySection);
        } catch {
            setError('Erro ao salvar seção.');
        } finally {
            setSaving(false);
        }
    };

    const saveRegion = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                sectionId: regForm.sectionId || null,
                slug: regForm.slug.trim() || slugify(regForm.titlePtBr || regForm.titleEn),
                titleEn: regForm.titleEn.trim(),
                titlePtBr: regForm.titlePtBr.trim(),
                thumbnailUrl: regForm.thumbnailUrl.trim(),
                bannerUrl: regForm.bannerUrl.trim(),
                sortOrder: Number(regForm.sortOrder) || 0,
                isVisible: regForm.isVisible,
            };
            if (regForm.id) await mountGuideService.updateRegion(regForm.id, payload);
            else await mountGuideService.createRegion(payload);
            invalidateMountGuideCache();
            await loadAll();
            setRegForm(emptyRegion);
        } catch {
            setError('Erro ao salvar região.');
        } finally {
            setSaving(false);
        }
    };

    const saveRarity = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                slug: rarForm.slug.trim() || slugify(rarForm.titlePtBr || rarForm.titleEn),
                titleEn: rarForm.titleEn.trim(),
                titlePtBr: rarForm.titlePtBr.trim(),
                accentColor: rarForm.accentColor.trim() || '#3b82f6',
                sortOrder: Number(rarForm.sortOrder) || 0,
                isVisible: rarForm.isVisible,
            };
            if (rarForm.id) await mountGuideService.updateRarity(rarForm.id, payload);
            else await mountGuideService.createRarity(payload);
            invalidateMountGuideCache();
            await loadAll();
            setRarForm(emptyRarity);
        } catch {
            setError('Erro ao salvar categoria de raridade.');
        } finally {
            setSaving(false);
        }
    };

    const saveMount = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!mountForm.regionId || !mountForm.rarityCategoryId) {
            setError('Selecione região e raridade.');
            return;
        }
        setSaving(true);
        try {
            JSON.parse(mountForm.detailsJson);
            const payload = {
                regionId: mountForm.regionId,
                rarityCategoryId: mountForm.rarityCategoryId,
                slug: mountForm.slug.trim() || slugify(mountForm.titlePtBr || mountForm.titleEn),
                titleEn: mountForm.titleEn.trim(),
                titlePtBr: mountForm.titlePtBr.trim(),
                iconUrl: mountForm.iconUrl.trim(),
                heroImageUrl: mountForm.heroImageUrl.trim(),
                descriptionEn: mountForm.descriptionEn.trim(),
                descriptionPtBr: mountForm.descriptionPtBr.trim(),
                detailsJson: mountForm.detailsJson.trim(),
                sortOrder: Number(mountForm.sortOrder) || 0,
                isPublished: mountForm.isPublished,
            };
            if (mountForm.id) await mountGuideService.updateMount(mountForm.id, payload);
            else await mountGuideService.createMount(payload);
            invalidateMountGuideCache();
            await loadAll();
            setMountForm({
                ...emptyMount,
                regionId: regions[0]?.id || 0,
                rarityCategoryId: rarities[0]?.id || 0,
            });
        } catch {
            setError('Erro ao salvar montaria (verifique o JSON de detalhes).');
        } finally {
            setSaving(false);
        }
    };

    const regionName = (id: number) => regions.find((r) => r.id === id)?.titlePtBr || `#${id}`;

    return (
        <div className="admin-guides-page">
            <h1>Guia de montarias</h1>
            <p className="admin-subtitle">
                Estrutura: <strong>Seções</strong> (agrupam regiões no grid) → <strong>Regiões</strong> (círculos) →{' '}
                <strong>Raridades</strong> (lendário, elite…) → <strong>Montarias</strong> (ficha completa em JSON).
                Página pública: <code>/game/montarias</code>
            </p>

            {error && <div className="admin-error">{error}</div>}

            <div className="admin-guides-tabs" style={{ flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                {(['sections', 'regions', 'rarities', 'mounts'] as Tab[]).map((t) => (
                    <button
                        key={t}
                        type="button"
                        className={tab === t ? 'active' : ''}
                        onClick={() => setTab(t)}
                    >
                        {t === 'sections' && 'Seções'}
                        {t === 'regions' && 'Regiões'}
                        {t === 'rarities' && 'Raridades'}
                        {t === 'mounts' && 'Montarias'}
                    </button>
                ))}
            </div>

            {tab === 'sections' && (
                <div className="admin-grid">
                    <div className="admin-list">
                        <h2>Seções</h2>
                        {loading ? (
                            <p>Carregando…</p>
                        ) : (
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>PT</th>
                                        <th>Slug</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sections.map((s) => (
                                        <tr key={s.id} onClick={() => setSecForm({ ...s })}>
                                            <td>{s.titlePtBr}</td>
                                            <td>
                                                <code>{s.slug}</code>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-small btn-danger"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (s.id && window.confirm('Excluir seção?')) {
                                                            await mountGuideService.deleteSection(s.id);
                                                            invalidateMountGuideCache();
                                                        }
                                                        await loadAll();
                                                    }}
                                                >
                                                    Excluir
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        <button type="button" className="btn btn-secondary" onClick={() => setSecForm(emptySection)}>
                            Nova seção
                        </button>
                    </div>
                    <form className="admin-form" onSubmit={saveSection}>
                        <h2>{secForm.id ? 'Editar' : 'Nova'} seção</h2>
                        <div className="form-row">
                            <label>Slug</label>
                            <input value={secForm.slug} onChange={(e) => setSecForm({ ...secForm, slug: e.target.value })} />
                        </div>
                        <div className="form-row-inline">
                            <div className="form-group">
                                <label>Título EN</label>
                                <input
                                    value={secForm.titleEn}
                                    onChange={(e) => setSecForm({ ...secForm, titleEn: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Título PT</label>
                                <input
                                    value={secForm.titlePtBr}
                                    onChange={(e) => setSecForm({ ...secForm, titlePtBr: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <label>Ordem</label>
                            <input
                                type="number"
                                value={secForm.sortOrder}
                                onChange={(e) => setSecForm({ ...secForm, sortOrder: Number(e.target.value) })}
                            />
                        </div>
                        <label>
                            <input
                                type="checkbox"
                                checked={secForm.isVisible}
                                onChange={(e) => setSecForm({ ...secForm, isVisible: e.target.checked })}
                            />{' '}
                            Visível
                        </label>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            Salvar
                        </button>
                    </form>
                </div>
            )}

            {tab === 'regions' && (
                <div className="admin-grid">
                    <div className="admin-list">
                        <h2>Regiões</h2>
                        {!loading && (
                            <table className="admin-table">
                                <tbody>
                                    {regions.map((r) => (
                                        <tr key={r.id} onClick={() => setRegForm({ ...r })}>
                                            <td>{r.titlePtBr}</td>
                                            <td>
                                                <code>{r.slug}</code>
                                            </td>
                                            <td>
                                                <button
                                                    type="button"
                                                    className="btn btn-small btn-danger"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (r.id && window.confirm('Excluir região e montarias?')) {
                                                            await mountGuideService.deleteRegion(r.id);
                                                            invalidateMountGuideCache();
                                                        }
                                                        await loadAll();
                                                    }}
                                                >
                                                    Excluir
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        <button type="button" className="btn btn-secondary" onClick={() => setRegForm(emptyRegion)}>
                            Nova região
                        </button>
                    </div>
                    <form className="admin-form" onSubmit={saveRegion}>
                        <h2>{regForm.id ? 'Editar' : 'Nova'} região</h2>
                        <div className="form-row">
                            <label>Seção (opcional)</label>
                            <select
                                value={regForm.sectionId ?? ''}
                                onChange={(e) =>
                                    setRegForm({
                                        ...regForm,
                                        sectionId: e.target.value ? Number(e.target.value) : null,
                                    })
                                }
                            >
                                <option value="">— Sem seção —</option>
                                {sections.map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.titlePtBr}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-row">
                            <label>Slug</label>
                            <input value={regForm.slug} onChange={(e) => setRegForm({ ...regForm, slug: e.target.value })} />
                        </div>
                        <div className="form-row-inline">
                            <div className="form-group">
                                <label>Título EN</label>
                                <input
                                    value={regForm.titleEn}
                                    onChange={(e) => setRegForm({ ...regForm, titleEn: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Título PT</label>
                                <input
                                    value={regForm.titlePtBr}
                                    onChange={(e) => setRegForm({ ...regForm, titlePtBr: e.target.value })}
                                    required
                                />
                            </div>
                        </div>
                        <div className="form-row">
                            <label>Miniatura (círculo)</label>
                            <input
                                value={regForm.thumbnailUrl}
                                onChange={(e) => setRegForm({ ...regForm, thumbnailUrl: e.target.value })}
                            />
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => e.target.files?.[0] && uploadField(e.target.files[0], 'thumbnailUrl')}
                            />
                        </div>
                        <div className="form-row">
                            <label>Banner da região</label>
                            <input
                                value={regForm.bannerUrl}
                                onChange={(e) => setRegForm({ ...regForm, bannerUrl: e.target.value })}
                            />
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => e.target.files?.[0] && uploadField(e.target.files[0], 'bannerUrl')}
                            />
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            Salvar
                        </button>
                    </form>
                </div>
            )}

            {tab === 'rarities' && (
                <div className="admin-grid">
                    <div className="admin-list">
                        <h2>Categorias de raridade</h2>
                        <table className="admin-table">
                            <tbody>
                                {rarities.map((r) => (
                                    <tr key={r.id} onClick={() => setRarForm({ ...r })}>
                                        <td style={{ borderLeft: `4px solid ${r.accentColor}` }}>{r.titlePtBr}</td>
                                        <td>
                                            <code>{r.slug}</code>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button type="button" className="btn btn-secondary" onClick={() => setRarForm(emptyRarity)}>
                            Nova raridade
                        </button>
                    </div>
                    <form className="admin-form" onSubmit={saveRarity}>
                        <h2>{rarForm.id ? 'Editar' : 'Nova'} raridade</h2>
                        <div className="form-row">
                            <label>Cor (hex)</label>
                            <input
                                type="color"
                                value={rarForm.accentColor}
                                onChange={(e) => setRarForm({ ...rarForm, accentColor: e.target.value })}
                            />
                        </div>
                        <div className="form-row">
                            <label>Slug</label>
                            <input value={rarForm.slug} onChange={(e) => setRarForm({ ...rarForm, slug: e.target.value })} />
                        </div>
                        <div className="form-row">
                            <label>Título PT</label>
                            <input
                                value={rarForm.titlePtBr}
                                onChange={(e) => setRarForm({ ...rarForm, titlePtBr: e.target.value })}
                                required
                            />
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            Salvar
                        </button>
                    </form>
                </div>
            )}

            {tab === 'mounts' && (
                <div className="admin-grid admin-grid--mount-guide">
                    <div className="admin-list">
                        <h2>Montarias</h2>
                        <table className="admin-table">
                            <tbody>
                                {mounts.map((m) => (
                                    <tr
                                        key={m.id}
                                        onClick={() => {
                                            const next = { ...m };
                                            const parsed = parseMountDetails(next.detailsJson);
                                            const hasDetails =
                                                (next.detailsJson?.length ?? 0) > 80 &&
                                                (parsed.grade != null || (parsed.stats?.length ?? 0) > 0);
                                            if (next.slug === 'bartolo' && !hasDetails) {
                                                next.detailsJson = stringifyMountDetails(BARTOLO_DETAILS);
                                            }
                                            setMountForm(next);
                                        }}
                                    >
                                        <td>{m.titlePtBr}</td>
                                        <td>{regionName(m.regionId)}</td>
                                        <td>
                                            <code>{m.slug}</code>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() =>
                                setMountForm({
                                    ...emptyMount,
                                    regionId: regions[0]?.id || 0,
                                    rarityCategoryId: rarities[0]?.id || 0,
                                })
                            }
                        >
                            Nova montaria
                        </button>
                    </div>
                    <form className="admin-form admin-form--mount-scroll" onSubmit={saveMount}>
                        <h2>{mountForm.id ? 'Editar' : 'Nova'} montaria</h2>
                        <div className="form-row-inline">
                            <div className="form-group">
                                <label>Região</label>
                                <select
                                    value={mountForm.regionId}
                                    onChange={(e) => setMountForm({ ...mountForm, regionId: Number(e.target.value) })}
                                    required
                                >
                                    {regions.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.titlePtBr}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Raridade</label>
                                <select
                                    value={mountForm.rarityCategoryId}
                                    onChange={(e) =>
                                        setMountForm({ ...mountForm, rarityCategoryId: Number(e.target.value) })
                                    }
                                    required
                                >
                                    {rarities.map((r) => (
                                        <option key={r.id} value={r.id}>
                                            {r.titlePtBr}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="form-row">
                            <label>Slug</label>
                            <input
                                value={mountForm.slug}
                                onChange={(e) => setMountForm({ ...mountForm, slug: e.target.value })}
                            />
                        </div>
                        <div className="form-row">
                            <label>Título PT</label>
                            <input
                                value={mountForm.titlePtBr}
                                onChange={(e) => setMountForm({ ...mountForm, titlePtBr: e.target.value })}
                                required
                            />
                        </div>
                        <div className="form-row">
                            <label>Ícone (lista / card da montaria)</label>
                            <input
                                value={mountForm.iconUrl}
                                onChange={(e) => setMountForm({ ...mountForm, iconUrl: e.target.value })}
                            />
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => e.target.files?.[0] && uploadField(e.target.files[0], 'iconUrl')}
                            />
                        </div>
                        <div className="form-row">
                            <label>Imagem grande (arte do monstro no topo da página)</label>
                            <input
                                value={mountForm.heroImageUrl}
                                onChange={(e) => setMountForm({ ...mountForm, heroImageUrl: e.target.value })}
                            />
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => e.target.files?.[0] && uploadField(e.target.files[0], 'heroImageUrl')}
                            />
                        </div>
                        <div className="form-row mount-guide-map-field-top">
                            <label>Mapa de localização (spawn)</label>
                            <p className="mount-guide-form-hint">
                                Imagem do mapa da região com o ponto de aparição — <strong>diferente</strong> do ícone
                                e da imagem grande. Aparece na seção «Área de aparição» do guia.
                            </p>
                            <input
                                value={mountDetails.mapImageUrl ?? ''}
                                onChange={(e) => patchMountDetails({ mapImageUrl: e.target.value })}
                                placeholder="URL do mapa"
                            />
                            <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) void uploadMapImage(file);
                                    e.target.value = '';
                                }}
                            />
                            {mapPreviewUrl && (
                                <div className="mount-guide-map-thumb-wrap">
                                    <img src={mapPreviewUrl} alt="Preview mapa" className="mount-guide-map-thumb" />
                                    <span className="mount-guide-form-hint">
                                        Marcador do spawn: ajuste em «Área de aparição e mapa» abaixo (clique no mapa).
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="form-row">
                            <label>Descrição PT</label>
                            <textarea
                                rows={3}
                                value={mountForm.descriptionPtBr}
                                onChange={(e) => setMountForm({ ...mountForm, descriptionPtBr: e.target.value })}
                            />
                        </div>
                        <div className="form-row mount-guide-details-admin">
                            <label>
                                Detalhes do fellow — stats, skills, mapa, captura (mesmas opções para cada montaria
                                nova)
                            </label>
                            <MountGuideMountForm
                                value={mountForm.detailsJson || ''}
                                onChange={(json) => setMountForm({ ...mountForm, detailsJson: json })}
                            />
                        </div>
                        <label>
                            <input
                                type="checkbox"
                                checked={mountForm.isPublished}
                                onChange={(e) => setMountForm({ ...mountForm, isPublished: e.target.checked })}
                            />{' '}
                            Publicado
                        </label>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            Salvar
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default AdminMountGuide;
